import React, {
    useCallback,
    useEffect,
    useState
} from "react";

import {
    MapContainer,
    TileLayer,
    Marker,
    Circle,
    useMap
} from "react-leaflet";

import L from "leaflet";

import {
    FiCheckCircle,
    FiClock,
    FiMapPin,
    FiNavigation,
    FiRefreshCw
} from "react-icons/fi";

import { motion } from "framer-motion";

import attendanceService from "../../services/attendanceService";

import "leaflet/dist/leaflet.css";
import "./active-attendance.css";


delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",

    iconUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",

    shadowUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});


function MapController({
    latitude,
    longitude
}) {

    const map = useMap();

    useEffect(() => {

        if (
            latitude &&
            longitude
        ) {

            map.setView(
                [
                    latitude,
                    longitude
                ],
                17
            );

        }

    }, [
        latitude,
        longitude,
        map
    ]);

    return null;
}


export default function ActiveAttendanceSessions() {

    const [
        sessions,
        setSessions
    ] = useState([]);

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        markingId,
        setMarkingId
    ] = useState(null);

    const [
        userLocation,
        setUserLocation
    ] = useState(null);

    const [
        locationLoading,
        setLocationLoading
    ] = useState(false);

    const [
        error,
        setError
    ] = useState("");


    const loadSessions =
        useCallback(
            async () => {

                try {

                    setLoading(true);

                    setError("");

                    const response =
                        await attendanceService.activeSessions();

                    setSessions(
                        response.sessions || []
                    );

                } catch (error) {

                    setError(
                        error?.response?.data?.message ||
                        error?.message ||
                        "Unable to load active attendance sessions."
                    );

                } finally {

                    setLoading(false);

                }

            },
            []
        );


    useEffect(() => {

        loadSessions();

    }, [loadSessions]);


    const getCurrentLocation =
        useCallback(() => {

            return new Promise(
                (
                    resolve,
                    reject
                ) => {

                    if (
                        !navigator.geolocation
                    ) {

                        reject(
                            new Error(
                                "Geolocation is not supported by your browser."
                            )
                        );

                        return;
                    }


                    navigator.geolocation.getCurrentPosition(

                        position => {

                            resolve({
                                latitude:
                                    position.coords.latitude,

                                longitude:
                                    position.coords.longitude,

                                accuracy:
                                    position.coords.accuracy
                            });

                        },

                        error => {

                            let message =
                                "Unable to get your current location.";

                            if (
                                error.code ===
                                error.PERMISSION_DENIED
                            ) {

                                message =
                                    "Location permission was denied. Please allow location access.";

                            }

                            if (
                                error.code ===
                                error.POSITION_UNAVAILABLE
                            ) {

                                message =
                                    "Your current location is unavailable.";

                            }

                            if (
                                error.code ===
                                error.TIMEOUT
                            ) {

                                message =
                                    "Getting your location timed out.";

                            }

                            reject(
                                new Error(message)
                            );

                        },

                        {
                            enableHighAccuracy: true,

                            timeout: 15000,

                            maximumAge: 0
                        }
                    );

                }
            );

        }, []);


    const markAttendance =
        async (session) => {

            try {

                setError("");

                setMarkingId(
                    session.id
                );

                setLocationLoading(true);


                const location =
                    await getCurrentLocation();


                setUserLocation({
                    latitude:
                        location.latitude,

                    longitude:
                        location.longitude,

                    accuracy:
                        location.accuracy
                });


                setLocationLoading(false);


                const response =
                    await attendanceService.selfMark(

                        session.id,

                        location.latitude,

                        location.longitude

                    );


                if (
                    response.success
                ) {

                    setSessions(
                        previous =>
                            previous.map(
                                item =>
                                    item.id === session.id
                                        ? {
                                            ...item,

                                            already_marked:
                                                true,

                                            attendance_status:
                                                "Present",

                                            marked_at:
                                                response
                                                    .attendance
                                                    ?.marked_at
                                        }
                                        : item
                            )
                    );

                    alert(
                        response.message ||
                        "Attendance marked successfully."
                    );

                }

            } catch (error) {

                setLocationLoading(false);

                setError(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unable to mark attendance."
                );

            } finally {

                setMarkingId(null);

            }

        };


    if (loading) {

        return (
            <div className="active-attendance-loading">
                Loading active attendance...
            </div>
        );

    }


    if (!sessions.length) {

        return (
            <div className="active-attendance-empty">

                <FiCheckCircle size={40} />

                <h3>
                    No Active Attendance
                </h3>

                <p>
                    There is currently no attendance session
                    available for you to mark.
                </p>

                <button
                    type="button"
                    onClick={loadSessions}
                >
                    <FiRefreshCw />

                    Refresh
                </button>

            </div>
        );

    }


    return (

        <section className="active-attendance">

            <div className="active-attendance-header">

                <div>

                    <span>
                        Attendance
                    </span>

                    <h2>
                        Active Sessions
                    </h2>

                    <p>
                        You can mark attendance only while
                        you are within the configured location.
                    </p>

                </div>


                <button
                    type="button"
                    onClick={loadSessions}
                    className="active-attendance-refresh"
                >
                    <FiRefreshCw />

                    Refresh
                </button>

            </div>


            {error && (

                <div className="active-attendance-error">

                    {error}

                </div>

            )}


            <div className="active-attendance-list">

                {sessions.map(session => (

                    <motion.div
                        key={session.id}
                        className="active-attendance-card"
                        initial={{
                            opacity: 0,
                            y: 20
                        }}
                        animate={{
                            opacity: 1,
                            y: 0
                        }}
                    >

                        <div className="active-attendance-card-header">

                            <div>

                                <h3>
                                    {session.title}
                                </h3>

                                <span>
                                    {session.type}
                                </span>

                            </div>


                            {session.already_marked && (

                                <div className="attendance-marked-badge">

                                    <FiCheckCircle />

                                    Present

                                </div>

                            )}

                        </div>


                        <div className="active-attendance-info">

                            <div>

                                <FiClock />

                                <span>
                                    {session.attendance_time}
                                </span>

                            </div>


                            <div>

                                <FiMapPin />

                                <span>
                                    Within{" "}
                                    {session.allowed_radius}
                                    m
                                </span>

                            </div>

                        </div>


                        {session.description && (

                            <p className="active-attendance-description">

                                {session.description}

                            </p>

                        )}


                        {session.latitude &&
                         session.longitude && (

                            <div className="active-attendance-map">

                                <MapContainer
                                    center={[
                                        session.latitude,
                                        session.longitude
                                    ]}
                                    zoom={17}
                                    scrollWheelZoom={false}
                                >

                                    <TileLayer
                                        attribution="&copy; OpenStreetMap contributors"
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />


                                    <Marker
                                        position={[
                                            session.latitude,
                                            session.longitude
                                        ]}
                                    />


                                    <Circle
                                        center={[
                                            session.latitude,
                                            session.longitude
                                        ]}
                                        radius={
                                            session.allowed_radius
                                        }
                                    />


                                    {userLocation && (

                                        <Marker
                                            position={[
                                                userLocation.latitude,
                                                userLocation.longitude
                                            ]}
                                        />

                                    )}


                                    <MapController
                                        latitude={
                                            userLocation?.latitude ||
                                            session.latitude
                                        }
                                        longitude={
                                            userLocation?.longitude ||
                                            session.longitude
                                        }
                                    />

                                </MapContainer>

                            </div>

                        )}


                        <div className="active-attendance-actions">

                            {session.already_marked ? (

                                <button
                                    type="button"
                                    className="attendance-success-button"
                                    disabled
                                >

                                    <FiCheckCircle />

                                    Attendance Marked

                                </button>

                            ) : (

                                <button
                                    type="button"
                                    className="attendance-mark-button"
                                    onClick={() =>
                                        markAttendance(session)
                                    }
                                    disabled={
                                        markingId === session.id
                                    }
                                >

                                    <FiNavigation />

                                    {markingId === session.id
                                        ? "Checking Location..."
                                        : "Mark My Attendance"
                                    }

                                </button>

                            )}

                        </div>


                        {locationLoading &&
                         markingId === session.id && (

                            <div className="attendance-location-message">

                                <FiNavigation />

                                Getting your current GPS location...

                            </div>

                        )}

                    </motion.div>

                ))}

            </div>

        </section>

    );

}